import {Exclude, Expose, instanceToPlain, plainToInstance, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {ObjectPreferenceEntity} from "./object-preference";
import {WaterGoPreferenceEntity} from "./water-go-preference";
import {OilGoPreferenceEntity} from "./oil-go-preference";
import {AddressEntity, AddressCodec} from "./address";

export const CompanyUnitValidator = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    companyId: t.string,
    regionId: t.string,
  }),
  t.partial({
    formalName: t.string,
    abbrName: t.string,
    address: AddressCodec,
    billingAddress: AddressCodec,
    contactPhone: t.string,
    active: t.boolean,
    doNotUse: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const CompanyUnitArrayValidator = t.array(
  CompanyUnitValidator
);

export type CompanyUnit = t.TypeOf<typeof CompanyUnitValidator>;
export type CompanyUnitArray = t.TypeOf<typeof CompanyUnitArrayValidator>;

@Exclude()
export class CompanyUnitEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Expose()
  companyId!: string

  @Expose()
  companyName?: string

  @Expose()
  regionId!: string

  @Expose()
  regionName?: string

  @Expose()
  formalName?: string

  @Expose()
  abbrName?: string

  @Expose()
  @Type(() => AddressEntity)
  address?: AddressEntity

  @Expose()
  @Type(() => AddressEntity)
  billingAddress?: AddressEntity

  @Expose()
  contactPhone?: string;

  @Expose()
  active?: boolean = true

  @Expose()
  doNotUse?: boolean = false

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  @Type(() => ObjectPreferenceEntity)
  objectPreferences?: ObjectPreferenceEntity[]

  @Expose()
  @Type(() => WaterGoPreferenceEntity)
  waterGoPreference?: WaterGoPreferenceEntity

  @Expose()
  @Type(() => OilGoPreferenceEntity)
  oilGoPreferenceEntity?: OilGoPreferenceEntity

  readonly entityType = 'COMPANY_UNIT';

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for CompanyUnit. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: CompanyUnit): CompanyUnitEntity {
    return plainToInstance(CompanyUnitEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<CompanyUnit> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(CompanyUnitValidator, object);
  }

  asTypeValue(): CompanyUnit {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(CompanyUnitValidator, object);
  }
}
